import { defaultConfig, type AcousticConfig } from './config';
import { crc16ccitt } from './crc';
import { Hamming } from './hamming';

export interface DecodedFrame {
  payload: string;
  payloadBytes?: Uint8Array;
  crcOk: boolean;
  corrected: number;
  length?: number;
  compressed?: boolean;
  rawSize?: number;
  error?: string;
}

export interface FrameSizeEstimate {
  transmittedBytes: number;
  variant: 'raw' | 'deflate';
  rawBytes: number;
}

const FLAG_COMPRESSED = 0x01;

const hasCompressionStream =
  typeof (globalThis as { CompressionStream?: unknown }).CompressionStream !==
  'undefined';

async function deflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart])
    .stream()
    .pipeThrough(new CompressionStream('deflate-raw'));
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'));
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

/**
 * Decide raw vs deflate and return the bytes that will go on the wire,
 * plus the flags-byte signalling which variant. Used by both encodeFrame
 * and the live frame-size estimator in the TX UI.
 */
async function preparePayload(
  rawBytes: Uint8Array,
): Promise<{ bytes: Uint8Array; flags: number; variant: 'raw' | 'deflate' }> {
  if (!hasCompressionStream) {
    return { bytes: rawBytes, flags: 0, variant: 'raw' };
  }
  try {
    const deflated = await deflateRaw(rawBytes);
    if (deflated.length < rawBytes.length) {
      return { bytes: deflated, flags: FLAG_COMPRESSED, variant: 'deflate' };
    }
  } catch {
    /* fall through to raw */
  }
  return { bytes: rawBytes, flags: 0, variant: 'raw' };
}

export const AcousticProtocol = {
  textToBytes(text: string): Uint8Array {
    return new Uint8Array(new TextEncoder().encode(text));
  },

  bytesToText(bytes: Uint8Array): string {
    return new TextDecoder().decode(bytes);
  },

  bytesToBits(bytes: Uint8Array): number[] {
    const bits: number[] = [];
    for (let i = 0; i < bytes.length; i++) {
      for (let b = 7; b >= 0; b--) {
        bits.push((bytes[i] >> b) & 1);
      }
    }
    return bits;
  },

  bitsToBytes(bits: number[]): Uint8Array {
    const bytes = new Uint8Array(Math.floor(bits.length / 8));
    for (let i = 0; i < bytes.length; i++) {
      let val = 0;
      for (let b = 0; b < 8; b++) {
        val = (val << 1) | bits[i * 8 + b];
      }
      bytes[i] = val;
    }
    return bytes;
  },

  bitsToSymbols(bits: number[], bitsPerSymbol: number): number[] {
    const padded = [...bits];
    while (padded.length % bitsPerSymbol !== 0) padded.push(0);
    const symbols: number[] = [];
    for (let i = 0; i < padded.length; i += bitsPerSymbol) {
      let val = 0;
      for (let b = 0; b < bitsPerSymbol; b++) {
        val = (val << 1) | padded[i + b];
      }
      symbols.push(val);
    }
    return symbols;
  },

  symbolsToBits(symbols: number[], bitsPerSymbol: number): number[] {
    const bits: number[] = [];
    for (const sym of symbols) {
      for (let b = bitsPerSymbol - 1; b >= 0; b--) {
        bits.push((sym >> b) & 1);
      }
    }
    return bits;
  },

  /**
   * Estimate the on-wire payload size for a given input text without building
   * the whole frame. Used by the live counter in the TX UI to keep it
   * consistent with what encodeFrame actually transmits.
   */
  async estimateFrameBytes(text: string): Promise<FrameSizeEstimate> {
    const raw = this.textToBytes(text);
    if (raw.length === 0) {
      return { transmittedBytes: 0, variant: 'raw', rawBytes: 0 };
    }
    const prepared = await preparePayload(raw);
    return {
      transmittedBytes: prepared.bytes.length,
      variant: prepared.variant,
      rawBytes: raw.length,
    };
  },

  async encodeFrame(
    text: string,
    config?: Partial<AcousticConfig>,
  ): Promise<number[]> {
    const cfg = { ...defaultConfig, ...config };
    const raw = this.textToBytes(text);
    const prepared = await preparePayload(raw);
    const payload = prepared.bytes;
    if (payload.length > cfg.maxPayload) {
      throw new Error('Payload too large (max ' + cfg.maxPayload + ' bytes)');
    }
    const lenBytes = new Uint8Array([
      (payload.length >> 8) & 0xff,
      payload.length & 0xff,
    ]);
    const flagsBytes = new Uint8Array([prepared.flags & 0xff]);
    const crcInput = new Uint8Array(
      lenBytes.length + flagsBytes.length + payload.length,
    );
    crcInput.set(lenBytes, 0);
    crcInput.set(flagsBytes, lenBytes.length);
    crcInput.set(payload, lenBytes.length + flagsBytes.length);
    const crcBytes = crc16ccitt(crcInput);

    const dataBits = [
      ...this.bytesToBits(lenBytes),
      ...this.bytesToBits(flagsBytes),
      ...this.bytesToBits(payload),
      ...this.bytesToBits(new Uint8Array(crcBytes)),
    ];

    const codedBits = Hamming.encode(dataBits);
    const dataSymbols = this.bitsToSymbols(codedBits, 2);

    return [...cfg.preamble, ...cfg.syncWord, ...dataSymbols, ...cfg.endWord];
  },

  async decodeFrame(codedBits: number[]): Promise<DecodedFrame> {
    const { data: allDataBits, corrected } = Hamming.decode(codedBits);
    if (allDataBits.length < 40) {
      return { payload: '', crcOk: false, corrected, error: 'Too short' };
    }
    const lenBits = allDataBits.slice(0, 16);
    const lenBytes = this.bitsToBytes(lenBits);
    const payloadLen = (lenBytes[0] << 8) | lenBytes[1];
    const flagsBits = allDataBits.slice(16, 24);
    const flagsBytes = this.bitsToBytes(flagsBits);
    const flags = flagsBytes[0];
    const payloadBitsStart = 24;
    const payloadBitsEnd = payloadBitsStart + payloadLen * 8;
    if (allDataBits.length < payloadBitsEnd + 16) {
      return { payload: '', crcOk: false, corrected, error: 'Incomplete' };
    }
    const payloadBits = allDataBits.slice(payloadBitsStart, payloadBitsEnd);
    const transmittedBytes = this.bitsToBytes(payloadBits);
    const crcBits = allDataBits.slice(payloadBitsEnd, payloadBitsEnd + 16);
    const crcReceived = this.bitsToBytes(crcBits);
    const crcInput = new Uint8Array(
      lenBytes.length + flagsBytes.length + transmittedBytes.length,
    );
    crcInput.set(lenBytes, 0);
    crcInput.set(flagsBytes, lenBytes.length);
    crcInput.set(transmittedBytes, lenBytes.length + flagsBytes.length);
    const crcExpected = crc16ccitt(crcInput);
    const crcOk =
      crcReceived[0] === crcExpected[0] && crcReceived[1] === crcExpected[1];

    const compressed = (flags & FLAG_COMPRESSED) !== 0;

    if (!crcOk) {
      return {
        payload: '',
        payloadBytes: transmittedBytes,
        crcOk,
        corrected,
        length: payloadLen,
        compressed,
      };
    }

    let finalBytes = transmittedBytes;
    if (compressed) {
      if (!hasCompressionStream) {
        return {
          payload: '',
          payloadBytes: transmittedBytes,
          crcOk: true,
          corrected,
          length: payloadLen,
          compressed,
          error: 'DecompressionStream not available',
        };
      }
      try {
        finalBytes = await inflateRaw(transmittedBytes);
      } catch (e) {
        return {
          payload: '',
          payloadBytes: transmittedBytes,
          crcOk: true,
          corrected,
          length: payloadLen,
          compressed,
          error: 'Decompression failed: ' + (e as Error).message,
        };
      }
    }

    return {
      payload: this.bytesToText(finalBytes),
      payloadBytes: finalBytes,
      crcOk,
      corrected,
      length: payloadLen,
      compressed,
      rawSize: finalBytes.length,
    };
  },
};
